import React from 'react';

import formatOptionValueTitle from 'product-personalizer/src/utils/formatOptionValueTitle';

import { ValueWithDisabled } from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ECommerceProductContent/eCommerceVariantUtils';
import ColorDot from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductControlContent/ProductControls/elements/ColorElement/ColorDot';
import Select from 'editor/src/component/Select';

import ECommerceProductControlOption from './ECommerceProductControlOption';

import styles from './index.module.scss';

interface Props {
  title: string;
  selectedValue: string;
  options: ValueWithDisabled[];
  onChange: (value: string) => void;
  // A long numeric axis (the page counts) reads better folded than as a wall of buttons.
  dropdown?: boolean;
}

function ECommerceProductControl({ title, selectedValue, options, onChange, dropdown }: Props) {
  // Unlike the catalogue Color control, there is no colour-shaped-key fallback here — option
  // values are arbitrary merchant strings. So a mixed control (some values with a hex, some
  // without) has no reason to become a colour control: require EVERY value to carry a hex,
  // otherwise a half-swatch row would look broken.
  const allValuesHaveHex = options.every((option) => !!option.colorHex);
  // A swatch hides the value's name, so name the selected one next to the title. Text options
  // already read their own name and keep the bare title.
  const selected = allValuesHaveHex ? options.find((option) => option.name === selectedValue) : undefined;
  const selectedTitle = selected && formatOptionValueTitle(selected.title ?? selected.name);

  if (dropdown) {
    return (
      <div className="cy-ecommerce-product-control">
        <div className={styles.controlTitle}>{title}</div>
        <Select
          options={options.map((option) => ({
            label: formatOptionValueTitle(option.title ?? option.name),
            value: option.name,
            disabled: option.disabled,
          }))}
          onSelect={(option) => option.value !== null && onChange(String(option.value))}
          selected={[selectedValue]}
        />
      </div>
    );
  }

  return (
    <div className="cy-ecommerce-product-control">
      <div className={styles.controlTitle}>
        {title}
        {selectedTitle && (
          <>
            {': '}
            <span className={styles.selectedTitle}>{selectedTitle}</span>
          </>
        )}
      </div>
      <div className={allValuesHaveHex ? styles.swatches : undefined}>
        {options.map((option) =>
          allValuesHaveHex ? (
            <button
              key={option.name}
              type="button"
              role="checkbox"
              aria-checked={selectedValue === option.name}
              aria-label={option.title ?? option.name}
              title={option.title ?? option.name}
              className={styles.swatchButton}
              disabled={option.disabled}
              onClick={() => onChange(option.name)}
            >
              <ColorDot
                colorHex={option.colorHex}
                texture={option.texture}
                selected={selectedValue === option.name}
                disabled={option.disabled}
                className={styles.swatchDot}
              />
            </button>
          ) : (
            <ECommerceProductControlOption
              key={option.name}
              option={option}
              selected={selectedValue === option.name}
              onChange={onChange}
            />
          ),
        )}
      </div>
    </div>
  );
}

export default React.memo(ECommerceProductControl);
